import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/rbac/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { ResponsiveLayout } from './components/layout/ResponsiveLayout';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { LegalNameRequiredModal } from './components/auth/LegalNameRequiredModal';
import { TermsAcceptanceGate } from './components/auth/TermsAcceptanceGate';

// Public pages
import LoginPage from './pages/LoginPage';
import PublicHomePage from './pages/PublicHomePage';
import SignupPage from './pages/auth/SignupPage';
import RegistrationSuccessPage from './pages/auth/RegistrationSuccessPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage';
import AccountDeactivatedPage from './pages/auth/AccountDeactivatedPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import IcaFlyerPage from './pages/IcaFlyerPage';

// Authenticated pages
import DashboardPage from './pages/dashboard/DashboardPage';
import ProfilePage from './pages/profile/ProfilePage';
import ManageUsersPage from './pages/ManageUsersPage';
import ManageOrganizationsPage from './pages/ManageOrganizationsPage';
import SettingsPage from './pages/SettingsPage';
import PermissionMatrixSettings from './pages/PermissionMatrixSettings';
import SystemSettingsPage from './pages/SystemSettingsPage';
import JudgeSheetTemplatesPage from './pages/JudgeSheetTemplatesPage';
import ReportsPage from './pages/ReportsPage';
import DocumentsPage from './pages/DocumentsPage';

// Phase 4.1 placeholder pages
import RodeosPage from './pages/rodeos/RodeosPage';
import RodeoDetailPage from './pages/rodeos/RodeoDetailPage';
import RodeoLivePage from './pages/rodeos/RodeoLivePage';
import BuddyGroupSetupPage from './pages/rodeos/BuddyGroupSetupPage';
import JackpotsPage from './pages/jackpots/JackpotsPage';
import JackpotDetailPage from './pages/jackpots/JackpotDetailPage';
import EventDetailPage from './pages/events/EventDetailPage';
import SupportPage from './pages/support/SupportPage';
import PaymentSuccessPage from './pages/payments/PaymentSuccessPage';
import PaymentCancelledPage from './pages/payments/PaymentCancelledPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import BulkRegistrationPage from './pages/admin/BulkRegistrationPage';
import UserDetailsPage from './pages/admin/UserDetailsPage';
import EntriesLedgerPage from './pages/admin/EntriesLedgerPage';
import MembershipVerificationPage from './pages/admin/MembershipVerificationPage';
import PriorityManagementPage from './pages/admin/PriorityManagementPage';
import DelegateDashboardPage from './pages/delegate/DelegateDashboardPage';
import DelegateJackpotsPage from './pages/delegate/DelegateJackpotsPage';
import DelegateDrawsPage from './pages/delegate/DelegateDrawsPage';
import DelegateLiveScoringPage from './pages/delegate/DelegateLiveScoringPage';
import DelegatePrizeManagementPage from './pages/delegate/DelegatePrizeManagementPage';
import DelegateManageScoringPage from './pages/delegate/DelegateManageScoringPage';
import JackpotEditPage from './pages/delegate/JackpotEditPage';
import DelegateRodeosPage from './pages/delegate/DelegateRodeosPage';
import RodeoWizard from './pages/delegate/RodeoWizard';
import BuddyGroupsPage from './pages/BuddyGroupsPage';
import StandingsPage from './pages/season/StandingsPage';
import SeasonRosterPage from './pages/season/SeasonRosterPage';
import SeasonContestantPage from './pages/season/SeasonContestantPage';
import NotificationsPage from './pages/NotificationsPage';

// Buddy group admin/delegate pages
import BuddyGroupListPage from './features/buddy-groups/pages/BuddyGroupListPage';
import AdminBuddyGroupDetailPage from './features/buddy-groups/pages/BuddyGroupDetailPage';
import BuddyGroupEditPage from './features/buddy-groups/pages/BuddyGroupEditPage';
import ConflictResolutionPage from './features/buddy-groups/pages/ConflictResolutionPage';

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  // Global loading state while checking session cookie
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      {/* Global gate — fires when an authenticated profile is missing a
          legal first or last name. Most common after Google OAuth signup
          where Google only ships a single-token name. The modal blocks
          all routing until the user supplies first + last. */}
      <LegalNameRequiredModal />
      {/* Global gate — forces acceptance of the current Standard License &
          Data-Usage Agreement. Covers Google OAuth users (no signup form to
          put the checkbox on) and re-prompts everyone when the agreement
          version is bumped. Defers to LegalNameRequiredModal above. */}
      <TermsAcceptanceGate />
      <Routes>
      {/* Public routes — redirect to dashboard if already authenticated */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/registration-success" element={<RegistrationSuccessPage />} />
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      {/* Public landing for soft-deactivated users. AuthContext routes here
          after logging them out when their session reveals is_active=false. */}
      <Route path="/account-deactivated" element={<AccountDeactivatedPage />} />

      {/* Standalone public marketing flyer for ICA to embed on their own
          site. Intentionally NOT linked from any nav, menu, or sitemap —
          reachable only by typing /icaflyer directly. Renders outside both
          the auth guard and the app layout shell (no header/sidebar), so it
          stands alone and screenshots cleanly. */}
      <Route path="/icaflyer" element={<IcaFlyerPage />} />

      {/* Stripe redirect-mode return URLs. Stripe Elements only redirects
          for a few async payment methods (e.g. some bank debits). Most
          flows confirm in-page; these routes catch the redirect-required
          cases and the webhook reconciles state on arrival. */}
      <Route path="/payments/success" element={<PaymentSuccessPage />} />
      <Route path="/payments/cancel" element={<PaymentCancelledPage />} />
      {/* Legacy aliases — kept so links from older builds / emails keep working. */}
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />

      {/* Public-browsable routes — visitors see a lighter header-only chrome,
          signed-in users get the full AppLayout (sidebar + breadcrumbs).
          Browse + view-only is open; registration actions inside these
          pages still gate on auth at the action level.
          The "/" landing page lives here too: visitors get PublicHomePage,
          signed-in users see DashboardPage (handled in the Redirects block). */}
      <Route element={<ResponsiveLayout />}>
        <Route path="/rodeos" element={<RodeosPage />} />
        <Route path="/rodeos/:rodeoId" element={<RodeoDetailPage />} />
        <Route path="/rodeos/:rodeoId/live" element={<RodeoLivePage />} />
        <Route path="/jackpots" element={<JackpotsPage />} />
        <Route path="/jackpots/:jackpotId" element={<JackpotDetailPage />} />
        <Route path="/events/:eventId" element={<EventDetailPage />} />
        {!isAuthenticated && <Route path="/" element={<PublicHomePage />} />}
      </Route>

      {/* Authenticated routes with layout shell */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Existing pages from template */}
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        {/* Association standings board: any association, any year, own row highlighted. */}
        <Route path="/standings" element={<StandingsPage />} />
        {/* The board used to live here; keep open tabs and bookmarks working. */}
        <Route path="/season-winnings" element={<Navigate to="/standings" replace />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/my/buddy-groups" element={<BuddyGroupsPage />} />
        <Route path="/my/buddy-groups/:groupId" element={<BuddyGroupsPage />} />
        {/* Rodeo-level buddy group setup (primary cardholder picks performance preferences) */}
        <Route path="/rodeos/:rodeoId/buddy-group/setup" element={<BuddyGroupSetupPage />} />
        <Route path="/support" element={<SupportPage />} />

        {/* Secretary & above — admin, delegate, secretary */}
        <Route element={<ProtectedRoute allowedRoles={['admin', 'delegate', 'secretary']} />}>
          <Route path="/delegate/live-scoring" element={<DelegateLiveScoringPage />} />
          <Route path="/delegate/live-scoring/:eventId" element={<DelegateLiveScoringPage />} />
          <Route path="/delegate/manage-scoring" element={<DelegateManageScoringPage />} />
        </Route>

        {/* Delegate & above — admin, delegate */}
        <Route element={<ProtectedRoute allowedRoles={['admin', 'delegate']} />}>
          <Route path="/delegate" element={<DelegateDashboardPage />} />
          <Route
            path="/delegate/membership-verifications"
            element={<MembershipVerificationPage />}
          />
          <Route path="/delegate/jackpots" element={<DelegateJackpotsPage />} />
          <Route path="/delegate/jackpots/:jackpotId/edit" element={<JackpotEditPage />} />
          <Route path="/delegate/rodeos" element={<DelegateRodeosPage />} />
          <Route path="/delegate/rodeos/create" element={<RodeoWizard />} />
          <Route path="/delegate/rodeos/:rodeoId/edit" element={<RodeoWizard />} />
          <Route path="/delegate/draws" element={<DelegateDrawsPage />} />
          <Route path="/delegate/draws/:eventId" element={<DelegateDrawsPage />} />
          {/* Prize Management — exposes contestant payment records, so it sits
              under the same admin+delegate guard as Draws (NOT the looser
              secretary-inclusive guard Live Scoring uses). */}
          <Route
            path="/delegate/prize-management"
            element={<DelegatePrizeManagementPage />}
          />
          {/* Cross-org entries ledger — delegate sees their org's, admin sees
              all. Permission scoping is enforced server-side. */}
          <Route path="/admin/entries-ledger" element={<EntriesLedgerPage />} />
          {/* Association season board. The association is in the query string
              on the list and in the path on the detail; either way the
              backend's org-scope check decides what a delegate may read. */}
          <Route path="/delegate/season-roster" element={<SeasonRosterPage />} />
          <Route
            path="/delegate/season-roster/:orgId/:userId"
            element={<SeasonContestantPage />}
          />
          {/* Buddy group admin/delegate pages */}
          <Route path="/rodeos/:rodeoId/buddy-groups" element={<BuddyGroupListPage />} />
          <Route path="/rodeos/:rodeoId/buddy-groups/conflicts" element={<ConflictResolutionPage />} />
          <Route path="/rodeos/:rodeoId/buddy-groups/:groupId" element={<AdminBuddyGroupDetailPage />} />
          <Route path="/rodeos/:rodeoId/buddy-groups/:groupId/edit" element={<BuddyGroupEditPage />} />
        </Route>

        {/* Admin-only routes */}
        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route path="/users" element={<ManageUsersPage />} />
          <Route path="/users/:userId" element={<UserDetailsPage />} />
          <Route path="/organizations" element={<ManageOrganizationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/permissions" element={<PermissionMatrixSettings />} />
          <Route path="/settings/system" element={<SystemSettingsPage />} />
          <Route path="/settings/system/judge-sheet-templates" element={<JudgeSheetTemplatesPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/bulk-registration" element={<BulkRegistrationPage />} />
          <Route path="/admin/priorities" element={<PriorityManagementPage />} />
        </Route>
      </Route>

      {/* Redirects: signed-in users at "/" land on dashboard. Unauth "/" is
          handled above by PublicHomePage. Catch-all sends signed-in users to
          dashboard, visitors to the public home. */}
      {isAuthenticated && (
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      )}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />}
      />
      </Routes>
    </>
  );
}

export default App;
